import React, { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getSafeReturnUrl } from '../utils/userAddingRoleToken';
import { signup, getElakayLang, getUserPUBToken } from "../includes/auth";
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { 

    FaEye, 
    FaEyeSlash, 
    FaUserPlus, 
    FaCheckCircle
} from "react-icons/fa";
import {
    MdEmail,
    MdLock,
    MdPerson,
    MdArrowForward,
    MdCheckCircle,
    MdOutlineRadioButtonUnchecked
} from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import logoelakayDiamant from '../includes/Layout/icon/logoelakayDiamant-256.png';
import './user.css';
import AuthPageNav, { AuthDestinationNotice } from '../includes/auth/AuthPageNav';
import { elakayAutoText } from "../includes/pageInside/dataElakay";
import { displayAuthError } from "../includes/auth/authErrors";

import {
    privacyPolicy,
    interpretationDefinitions,
    CollectingUsingYourPersonalData,
    ContactUs,
    TermsConditions
} from '../includes/pageInside/conditionTherm'

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["signup"]?.[key] || fallback;

const Signup = (props) => {
    const [returnQuery] = useSearchParams();
    const eventReturn = getSafeReturnUrl(returnQuery.get('returnUrl'));
    const signinPath = eventReturn?.startsWith('/user-step?')
      ? `/signin?returnUrl=${encodeURIComponent(eventReturn)}`
      : '/signin';

    const { 
        infoSource,
        
        // === HEADER ANIMATIONS ===
        enableHeaderAnimations = false,
        enableHeaderShapes = false,
        enableLogoAnimation = false,
        enableLogoPulse = false,
        enableLogoHover = false,
        enableDecorationAnimations = false,
        
        // === PARTICLES ===
        enableParticles = false,
        particlesCount = 0,
        
        // === CARD ===
        enableCardEntrance = false,
        enableCardGlow = false,
        
        // === FORM HEADER / ICON ===
        enableTitleGlow = false,
        enableIconAnimation = false,
        
        // === FORM INPUTS ===
        enableFormGroupFade = false,
        enableInputFocus = false,
        enablePasswordToggle = true,
        enablePasswordStrength = true,
        enablePasswordMatch = true,
        
        // === CHECKBOX / TERMS ===
        enableCheckboxAnimation = false,
        enableTermsLinkHover = false,
        
        // === BUTTONS ===
        enableButtonHover = false,
        enableButtonIcon = false,
        
        // === LINKS ===
        enableLinkHover = false,
        
        // === ALERTS ===
        enableAlertSlide = false,
        enableLoadingDots = false,
        enableSuccessAlert = true,
        enableSuccessIcon = true,
        
        // === FOOTER ===
        enableFooterWaves = false,
        enableFooterLinkHover = false
    } = props;
  __setElakayInfoSource(infoSource);
    
    const { obj } = getElakayLang();

    const [showPassword, setShowPassword] = useState(true);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(true);
       const [showConditions, setConditions] = useState(false);
    const [values, setValues] = useState({
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        passwordConfirmation: "",
        agreeTerms: false,
        error: "",
        errorCode: null,
        success: false,
        loading: false
    });

    let navigate = useNavigate();
    const {
        firstname,
        lastname,
        email,
        password,
        passwordConfirmation,
        agreeTerms,
        success,
        error,
        loading
    } = values;

    const firstnameRef = useRef(null);
    const lastnameRef = useRef(null);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const passwordConfirmationRef = useRef(null);
    const termsRef = useRef(null);

    const scrollToError = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            ref.current.focus?.();
        }
    };

    const handleChange = name => event => {
        setValues({ ...values, error: false, errorCode: null, [name]: event.target.value });
    };

    const handleCheckbox = name => event => {
        setValues({ ...values, [name]: event.target.checked });
    };

    const clickSubmit = event => {
        event.preventDefault();

        // Validation — each check scrolls straight to the field that needs attention
        if (!firstname.trim()) {
            setValues({ ...values, error: "", errorCode: "FIRSTNAME_REQUIRED" });
            scrollToError(firstnameRef);
            return;
        }

        if (!lastname.trim()) {
            setValues({ ...values, error: "", errorCode: "LASTNAME_REQUIRED" });
            scrollToError(lastnameRef);
            return;
        }

        if (!email.trim()) {
            setValues({ ...values, error: "", errorCode: "EMAIL_REQUIRED" });
            scrollToError(emailRef);
            return;
        }

        // 7 = the User model's real minimum (a 6-character password was
        // accepted here, then always refused by the server).
        if (password.length < 7) {
            setValues({ ...values, error: "", errorCode: "PASSWORD_TOO_SHORT" });
            scrollToError(passwordRef);
            return;
        }

        if (password !== passwordConfirmation) {
            setValues({ ...values, error: "", errorCode: "PASSWORD_CONFIRMATION_MISMATCH" });
            scrollToError(passwordConfirmationRef);
            return;
        }

        if (!agreeTerms) {
            setValues({ ...values, error: "", errorCode: "TERMS_REQUIRED" });
            scrollToError(termsRef);
            return;
        }

        setValues({ ...values, error: false, errorCode: null, loading: true });
        
        signup({ 
            firstname,
            lastname,
            email, 
            password,
            passwordConfirmation,
            language: obj 
        }, getUserPUBToken()?.pubToken)
        .then(data => {
            if (data.error) {
                setValues({ 
                    ...values, 
                    error: data.error, errorCode: data.code || null,
                    loading: false,
                    success: false
                });
                // Server-side field errors (EMAIL_ALREADY_EXISTS, PASSWORD_TOO_WEAK...)
                // bring the matching input into view, like the local checks above.
                const fieldRefs = { firstname: firstnameRef, lastname: lastnameRef, email: emailRef, password: passwordRef, passwordConfirmation: passwordConfirmationRef };
                if (fieldRefs[data.field]) scrollToError(fieldRefs[data.field]);
            } else {
                setValues({
                    firstname: "",
                    lastname: "",
                    email: "",
                    password: "",
                    passwordConfirmation: "",
                    agreeTerms: false,
                    error: "",
                    success: true,
                    loading: false
                });
            }
        });
    };

    const getPasswordStrength = () => {
        if (password.length === 0) return '';
        if (password.length < 6) return 'weak';
        if (password.length < 10) return 'medium';
        return 'strong';
    };

    const getPasswordStrengthText = () => {
        if (password.length === 0) return '';
        if (password.length < 6) return __elakayText("t011", "Weak");
        if (password.length < 10) return __elakayText("t012", "Medium");
        return __elakayText("t013", "Strong");
    };

    const clickelakay = () => { 
        return window.location.href = "/";
    };

    // Re-computed on every render: a language switch re-translates an error
    // already displayed (errorCode + active language), no new request.
    const shownError = displayAuthError(values.errorCode, error);

    const showError = () => (
        <div
            className={`sign-alert sign-alert-danger ${!enableAlertSlide ? 'no-slide' : ''}`}
            style={{ display: shownError ? "" : "none" }}
            role="alert"
        >
            {shownError}
        </div>
    );

    const showSuccess = () =>
        success && enableSuccessAlert && (
            <div className={`sign-alert sign-alert-success ${!enableAlertSlide ? 'no-slide' : ''}`}>
                {enableSuccessIcon && (
                    <FaCheckCircle className="sign-alert-icon" />
                )}
                <div className="sign-alert-content">
                    <p className="sign-alert-text">
                        {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).successCreated || "Account created successfully!"}
                    </p>
                    <p className="sign-alert-subtitle">
                        {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).checkEmail || "Please check your email to activate your account."}
                    </p>
                </div>
            </div>
        );

    const showLoading = () =>
        loading && (
            <div className={`sign-alert sign-alert-loading ${!enableAlertSlide ? 'no-slide' : ''}`}>
                {enableLoadingDots && (
                    <div className="sign-loading-dots">
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                    </div>
                )}
                <span className="sign-loading-text">
                    {infoSource.language(infoSource, "title", "loanding") || elakayAutoText("h_b04ba49f848624", "Loading...")}
                </span>
            </div>
        );

    const signUpForm = () => (
        <div className={`sign-page-wrapper ${!enableHeaderAnimations ? 'no-animations' : ''}`}>
            {/* Animated Header */}
            <header className={`sign-header ${!enableHeaderAnimations ? 'no-header-animation' : ''}`}>
                {enableHeaderShapes && (
                    <div className="sign-header-bg-shapes">
                        <div className="sign-shape sign-shape-1"></div>
                        <div className="sign-shape sign-shape-2"></div>
                        <div className="sign-shape sign-shape-3"></div>
                        <div className="sign-shape sign-shape-4"></div>
                    </div>
                )}
                
                <div className="sign-header-content">
                    <div 
                        className={`sign-logo ${!enableLogoHover ? 'no-hover' : ''}`}
                        onClick={() => clickelakay()}
                    >
                        <div className="sign-logo-icon-wrapper">
                            <img 
                                className={`sign-logo-image ${!enableLogoAnimation ? 'no-bounce' : ''}`} 
                                src={logoelakayDiamant} 
                                width="40" 
                                alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).signup.t001}
                            />
                            {enableLogoPulse && <div className="sign-logo-pulse"></div>}
                        </div>
                        <span className="sign-logo-text">{__elakayText("t007", "elakay")}</span>
                    </div>
                    
                    <AuthPageNav />
                    {enableDecorationAnimations && (
                        <div className="sign-header-decorations">
                            <BiWorld className="sign-decoration-icon sign-decoration-1" />
                            <div className="sign-decoration-line"></div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="sign-main-content">
                {enableParticles && (
                    <div className="sign-particles">
                        {[...Array(particlesCount)].map((_, i) => (
                            <div key={i} className="sign-particle" style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 3}s`,
                                animationDuration: `${3 + Math.random() * 2}s`
                            }}></div>
                        ))}
                    </div>
                )}

                <div className="sign-form-container">
                    <div className={`sign-form-card ${!enableCardEntrance ? 'no-entrance' : ''}`}>
                        {enableCardGlow && <div className="sign-form-card-glow"></div>}
                        
                        <form className="sign-form" onSubmit={clickSubmit}>
                            {/* Icon Header */}
                            <div className="sign-form-header">
                                <div className={`sign-signup-icon-wrapper ${!enableIconAnimation ? 'no-animation' : ''}`}>
                                    <div className={`sign-signup-icon-circle ${!enableIconAnimation ? 'no-pulse' : ''}`}>
                                        <FaUserPlus className="sign-signup-icon" />
                                    </div>
                                </div>
                                <h1 className={`sign-form-title ${!enableTitleGlow ? 'no-glow' : ''}`}>
                                    {infoSource.laguangeElakay('Button', infoSource.languageAPI).creatAccount || "Create Account"}
                                </h1>
                                <p className="sign-form-subtitle">{__elakayText("t003", "Join us and start your journey today")}</p>
                            </div>

                            <AuthDestinationNotice />
                            {showLoading()}
                            {showError()}
                            {showSuccess()}

                            {/* ========== FIRST NAME ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).firstName || "First Name"} 
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdPerson className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={firstnameRef}
                                        onChange={handleChange("firstname")}
                                        type="text"
                                        value={firstname}
                                        name="firstname"
                                        id="inputFirstname"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`}
                                        placeholder={infoSource.laguangeElakay('label', infoSource.languageAPI).firstName || "Enter your first name"}
                                        required
                                    />
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_signup_hint_firstname", "Your legal first name, as it should appear on your account")}</span>
                            </div>

                            {/* ========== LAST NAME ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).lastName || "Last Name"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdPerson className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={lastnameRef}
                                        onChange={handleChange("lastname")}
                                        type="text"
                                        value={lastname}
                                        name="lastname"
                                        id="inputLastname"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`}
                                        placeholder={infoSource.laguangeElakay('label', infoSource.languageAPI).lastName || "Enter your last name"}
                                        required
                                    />
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_signup_hint_lastname", "Your legal last name, as it should appear on your account")}</span>
                            </div>

                            {/* ========== EMAIL ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).email || "Email Address"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdEmail className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={emailRef}
                                        onChange={handleChange("email")}
                                        type="email"
                                        value={email}
                                        name="email"
                                        id="inputEmail"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`}
                                        placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).signup.t002}
                                        required
                                    />
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_signup_hint_email", "We'll send a confirmation link to this address to activate your account")}</span>
                            </div>

                            {/* ========== PASSWORD ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).Password || "Password"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdLock className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={passwordRef}
                                        onChange={handleChange("password")}
                                        type={showPassword ? "password" : "text"}
                                        value={password}
                                        name="password"
                                        id="inputPassword"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`}
                                        placeholder={infoSource.laguangeElakay('label', infoSource.languageAPI).Password || "Create a strong password"}
                                        required
                                    />
                                    {enablePasswordToggle && (
                                        <button
                                            type="button"
                                            className="sign-password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEye /> : <FaEyeSlash />}
                                        </button>
                                    )}
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_signup_hint_password", "At least 7 characters")}</span>

                                {/* Password Strength Meter */}
                                {enablePasswordStrength && (
                                    <div className="sign-password-strength">
                                        <div className="sign-strength-bar">
                                            <div className={`sign-strength-fill ${getPasswordStrength()}`}></div>
                                        </div>
                                        <span className={`sign-strength-text ${getPasswordStrength()}`}>
                                            {getPasswordStrengthText()}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* ========== PASSWORD CONFIRMATION ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).confirmPassword || "Confirm Password"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdLock className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={passwordConfirmationRef}
                                        onChange={handleChange("passwordConfirmation")}
                                        type={showPasswordConfirmation ? "password" : "text"}
                                        value={passwordConfirmation}
                                        name="passwordConfirmation"
                                        id="inputPasswordConfirmation"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`} 
                                        placeholder={infoSource.laguangeElakay('label', infoSource.languageAPI).confirmPassword || "Re-enter your password"}
                                        required  
                                    />
                                    {enablePasswordToggle && (
                                        <button 
                                            type="button"
                                            className="sign-password-toggle" 
                                            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                        >
                                            {showPasswordConfirmation ? <FaEye /> : <FaEyeSlash />}
                                        </button>
                                    )}
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_signup_hint_pwdconfirm", "Re-enter the same password")}</span>

                                {/* Password Match Indicator */}
                                {enablePasswordMatch && passwordConfirmation && (
                                    <div style={{ 
                                        marginTop: '8px', 
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: password === passwordConfirmation ? '#34c759' : '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {password === passwordConfirmation ? 
                                            elakayAutoText("h_1362e1643cfc86", "✓ Passwords match") : 
                                            elakayAutoText("h_5507efd81e21d2", "✗ Passwords do not match")
                                        }
                                    </div>
                                )}
                            </div>

                            {/* ========== TERMS CHECKBOX ========== */}
                            <div ref={termsRef} className={`sign-terms-wrapper ${agreeTerms ? 'active' : 'inactive'} ${!enableCheckboxAnimation ? 'no-animation' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="sign-checkbox-input"
                                    id="terms"
                                    checked={agreeTerms}
                                    onChange={handleCheckbox("agreeTerms")}
                                    aria-checked={agreeTerms}
                                    required
                                />
                                <label htmlFor="terms" className="sign-terms-label">
                                    {agreeTerms ? <MdCheckCircle className="sign-checkbox-icon" /> : <MdOutlineRadioButtonUnchecked className="sign-checkbox-icon" />}{' '}
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).agreeTerms || "I agree to the"}{' '}
                                    <Link 
                                        to="/help-contact" 
                                        className={`sign-terms-link ${!enableTermsLinkHover ? 'no-hover' : ''}`}
                                    >
                                        {infoSource.laguangeElakay('label', infoSource.languageAPI).terms || "Terms of Service"}
                                    </Link>
                                    {' '}{infoSource.laguangeElakay('label', infoSource.languageAPI).and || "and"}{' '}
                                    <Link 
                                        to="#" onClick={() => setConditions(true)}
                                        className={`sign-terms-link ${!enableTermsLinkHover ? 'no-hover' : ''}`}
                                    >
                                        {infoSource.laguangeElakay('label', infoSource.languageAPI).privacy || "Privacy Policy"}
                                    </Link>
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                            </div>

                            <input type="hidden" name="verication" placeholder="" />

                            {/* ========== SUBMIT BUTTON ========== */}
                            <button 
                                type="submit"
                                className={`sign-btn-primary sign-btn-full ${enableButtonIcon ? 'sign-btn-animated' : ''} ${!enableButtonHover ? 'no-hover' : ''}`}
                                disabled={loading}
                            >
                                <span>{infoSource.laguangeElakay('Button', infoSource.languageAPI).creatAccount || "Create Account"}</span>
                                {enableButtonIcon && <MdArrowForward className="sign-btn-icon" />}
                            </button>

                            {/* ========== DIVIDER ========== */}
                            <div className="sign-divider">
                                <span className="sign-divider-text">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).alreadyAccount || "Already have an account?"}
                                </span>
                            </div>

                            {/* ========== SIGNIN LINK ========== */}
                            <Link 
                                className={`sign-btn-secondary sign-btn-full ${!enableButtonHover ? 'no-hover' : ''}`}
                                to={signinPath}
                            >
                                {infoSource.laguangeElakay('Button', infoSource.languageAPI).signin || "Sign In"}
                            </Link>
                        </form>
                    </div>
                </div>
            </main>

            {/* Animated Footer */}
            <footer className={`sign-footer ${!enableFooterWaves ? 'no-waves' : ''}`}>
                {enableFooterWaves && (
                    <div className="sign-footer-waves">
                        <div className="sign-wave sign-wave-1"></div>
                        <div className="sign-wave sign-wave-2"></div>
                        <div className="sign-wave sign-wave-3"></div>
                    </div>
                )}
                
                <div className="sign-footer-content">
                    <p className="sign-footer-text">
                        © {new Date().getFullYear()}{__elakayText("t004", "elakay. All rights reserved.")}</p>
                    <div className="sign-footer-links">
                        <Link 
                            to="#" onClick={() => setConditions(true)}
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t008", "Privacy")}</Link>
                        <span className="sign-footer-separator">•</span>
                        <Link
                           to="/help-contact"
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t009", "Terms")}</Link>
                        {/* <span className="sign-footer-separator">•</span>
                        <Link
                            to="#" onClick={() => setConditions(true)}
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t010", "Help")}</Link> */}
                    </div>
                </div>
            </footer>
        </div>
    );

    const redirectUser = () => {
        if (success) {
            setTimeout(() => {
                navigate(signinPath);
            }, 3000);
        }
    };

        const AddTermsAndConditions = () => (
        <div className="contaire">
            <div className="row justify-content-center " >
                <Modal
                    size="lg"
                    show={showConditions}
                    onHide={() => setConditions(false)}
                    dialogClassName="modal-90w"
                    aria-labelledby="example-custom-modal-styling-title"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="example-custom-modal-styling-title">
                            <h2 className="text-center">{elakayAutoText("h_894031ed8d341b", "Terms and Conditions")}</h2>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {TermsConditions()}
                        {privacyPolicy()}
                        {interpretationDefinitions()}
                        {CollectingUsingYourPersonalData()}
                        {ContactUs()}
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    )

    return (
        <div className="sign-app-wrapper">
            {signUpForm()}
            {redirectUser()}
            {AddTermsAndConditions()}
        </div>
    );
};

export default Signup;
